const API_BASE_URL = process.env.REACT_APP_API_URL;

// Get all services (for admin dashboard)
export async function getAllServices(params = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
    
    const url = `${API_BASE_URL}/api/services${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch services');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Get all services error:', error);
    throw error;
  }
}

// Get active services only (for frontend pages)
export async function getActiveServices(params = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    
    const url = `${API_BASE_URL}/api/services/active${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Check if response is HTML (server error page)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      console.error('API Call - Received HTML response instead of JSON');
      throw new Error('Server connection error. Please ensure the backend server is running on port 5000.');
    }

    if (!response.ok) {
      let errorMessage = 'Failed to fetch active services';
      try {
        const error = await response.json();
        console.error('API Call - Get active services error response:', error);
        errorMessage = error.message || errorMessage;
      } catch (parseError) {
        console.error('API Call - Failed to parse error response:', parseError);
        errorMessage = `Server error (${response.status})`;
      }
      throw new Error(errorMessage);
    }

    let result;
    try {
      result = await response.json();
      return result;
    } catch (parseError) {
      console.error('API Call - Failed to parse active services response:', parseError);
      throw new Error('Invalid server response format');
    }
  } catch (error) {
    console.error('API Call - Get active services fetch error:', error);
    
    // Provide user-friendly error messages
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Unable to connect to server. Please check if the backend is running.');
    }
    
    throw error;
  }
}

// Get single service by ID
export async function getServiceById(serviceId) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/services/${serviceId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch service');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Get service error:', error);
    throw error;
  }
}

// Create new service
export async function createService(serviceData) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/services`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(serviceData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create service');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Create service error:', error);
    throw error;
  }
}

// Update service
export async function updateService(serviceId, serviceData) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/services/${serviceId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(serviceData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update service');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Update service error:', error);
    throw error;
  }
}

// Soft delete service (set isActive to false)
export async function deleteService(serviceId, adminId) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/services/${serviceId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ adminId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete service');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Delete service error:', error);
    throw error;
  }
}

// Hard delete service (permanently remove)
export async function hardDeleteService(serviceId) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/services/${serviceId}/hard`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to permanently delete service');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Hard delete service error:', error);
    throw error;
  }
}

// Get services by category
export async function getServicesByCategory(category, activeOnly = true) {
  const fetchFunction = activeOnly ? getActiveServices : getAllServices;
  return await fetchFunction({ category });
}

// Update service order
export async function updateServiceOrder(category, serviceOrder) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/services/update-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        category,
        serviceOrder
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update service order');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Update service order error:', error);
    throw error;
  }
}